import {
    Box,
    ButtonLink,
    Callout,
    IconChevronLeftRegular,
    IconWarningRegular,
    Text2,
    Text6,
    ThemeVariant,
    RowList,
    NavigationBar,
    Stack,
    Text3,
    Spinner,
    skinVars,
    useDialog,
    IconInformationUserRegular,
} from '@telefonica/mistica';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import CardWithSwitch, { ICardSwitchEvent } from '../../../components/CartWithSwitch';
import SkeletonMobile from '../../../components/SkeletonMobile';
import { DataRestriction, ISwitch } from '../../../models/ConfigureLine';
import { getAllRestrictions, getSIMSInfo, updateRestriction } from '../../../services/ConfigureLineService';
import { formatMobileNumber } from '../../../utils/dataTransformation/unitsConversion';
import navigateParams from '../../../utils/navigation/navigateParams';
import { showSnackbar } from '../../../utils/store/features/user';
import { AxiosError } from 'axios';
import './index.scss';
import { handleErrorMessage } from '../../../utils/dataTransformation/errorMesssages';
import { itsMoreThanXminutes } from '../../../utils/dataTransformation/dateConversions';
import { getRoleLine } from '../../../utils/dataTransformation/productConsumption';
import { TrackingEvents } from '../../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../../utils/navigation/controlDevices';
import useUserlight from '../../../hooks/useUserLight';
import { useConfigureLineContext } from '../../../context/configure-line-context/ConfigureLineContext';
import { useQuery } from '@tanstack/react-query';
import BreadCrumbsWrapper from '../../../components/BreadcrumbsWrapper';

const Roaming: React.FC = () => {
    const isLight = useUserlight();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const [loading, setLoading] = useState<boolean>(true);
    const consumptions = JSON.stringify(window.localStorage.getItem('storages'));
    const { trackingScreenView } = new TrackingEvents();
    const dispatch = useDispatch();
    const [data, setData] = useState<ISwitch[]>([]);
    const { dialog } = useDialog();
    const { lineActive, isConvergent } = useConfigureLineContext();
    const { data: infoSIM } = useQuery({
        queryKey: ['sims-info', lineActive],
        queryFn: () => getSIMSInfo(lineActive),
        enabled: !!lineActive,
    });

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
        if (!infoSIM?.productId || infoSIM?.productId.trim() === '') return;
        getAllRestrictions(infoSIM?.productId!)
            .then((results: any) => {
                const dataRoaming = results.data.filter((item: any) => {
                    return item.restrictionType === 'ROAMING';
                });
                let dataSetRoaming: ISwitch[] = [];

                dataRoaming.forEach((element: any) => {
                    dataSetRoaming.push({
                        restrictionSpecId: element.restrictionSpecId,
                        title: element.title,
                        subtitle: element.description,
                        state: element.active,
                    });
                });
                setData(dataSetRoaming);
                setLoading(false);
            })
            .catch((err: AxiosError) => {
                console.error(err);
                setLoading(false);
            });
    }, [dispatch]);

    const handleSwitches = (card: ICardSwitchEvent, index: number) => {
        const { state } = card;
        if (isLight) {
            dispatch(showSnackbar());
        } else {
            const dataRestriction: DataRestriction = {
                productId: infoSIM?.productId!,
                restrictionSpecId: card.index,
                activate: state,
            };
            if (window.localStorage.getItem('timestamp')) {
                const storage: any = JSON.parse(window.localStorage.getItem('timestamp')!);

                if (itsMoreThanXminutes(storage?.time, 4)) {
                    updateRestriction(dataRestriction)
                        .then((results: any) => {
                            window.localStorage.setItem(
                                'timestamp',
                                JSON.stringify({ id: card.index, time: `${new Date()}` }),
                            );
                            sendToNative(state);
                        })
                        .catch((err: AxiosError) => {
                            console.error(err);
                            const error = handleErrorMessage(err);
                            const { pathname } = location;
                            const _error = { ...error, pathname };
                            navigate('/error', { state: _error });
                        });
                } else {
                    dialog({
                        asset: <IconWarningRegular color={skinVars.colors.feedbackErrorBackground} size={64} />,
                        title: t('requestNotAvaiable'),
                        message: (() => {
                            return (
                                <Stack space={16}>
                                    <Text3 light color={skinVars.colors.borderHigh}>
                                        {t('notPosibleFinishedRequest')}
                                    </Text3>
                                    <Text3 light color={skinVars.colors.borderHigh}>
                                        {t('call1551')}
                                    </Text3>
                                </Stack>
                            );
                        })() as any,
                        onAccept() {},
                        acceptText: t('buttonUnderstoodFeedback'),
                    });
                }
            } else {
                updateRestriction(dataRestriction)
                    .then((results: any) => {
                        window.localStorage.setItem(
                            'timestamp',
                            JSON.stringify({ id: card.index, time: `${new Date()}` }),
                        );
                        sendToNative(state);
                    })
                    .catch((err: AxiosError) => {
                        console.error(err);
                        const error = handleErrorMessage(err);
                        const { pathname } = location;
                        const _error = { ...error, pathname };
                        navigate('/error', { state: _error });
                    });
            }
        }
    };

    const sendToNative = (state: boolean) => {
        const action = state ? 'activated' : 'desactivated';
        const type = state ? 'feedback-info' : 'feedback-error';
        const literals: { [key: string]: any } = {
            activated: {
                title: t('activationRequested'),
                subtitle: t('descriptionRoamingActivated'),
            },
            desactivated: {
                title: t('desactivationRequested'),
                subtitle: t('descriptionRoamingDeactivated'),
            },
        };
        const queryParams = {
            state: 'INFO',
            title: literals[action].title,
            subtitle: literals[action].subtitle,
        };

        const _productId = infoSIM?.productId || location.state.productId;
        const _phone = infoSIM?.number || location.state.phone;
        const _multisim = infoSIM?.multiSims || location.state.multisim;
        if (navigateParams(type, `/configure-line/roaming/feedback/${action}`, queryParams, {})) {
            navigate(`/configure-line/roaming/feedback/${action}`, {
                state: { productId: _productId, phone: _phone, multisim: _multisim },
            });
        }
    };

    const body = () => {
        return (
            <>
                <RowList>
                    {data.map((card: ISwitch, index: number) => {
                        return (
                            <div className='roaming-page__card' key={index}>
                                <CardWithSwitch
                                    index={card.restrictionSpecId}
                                    title={card.title}
                                    subtitle={card.subtitle}
                                    state={card.state}
                                    onHandleSwicth={(ev: any) => handleSwitches(ev, index)}
                                />
                            </div>
                        );
                    })}
                </RowList>
                <Box className='roaming-page__callout'>
                    <Callout
                        asset={<IconInformationUserRegular color={skinVars.colors.brand} size={24} />}
                        description={t('calloutRoaming')}
                        buttonLink={
                            <ButtonLink
                                onPress={() => {
                                    const url =
                                        'https://o2online.es/ayuda/consejos-para-evitar-cobros-no-deseados-en-roaming/';
                                    if (navigateParams('modeExternal', url, {}, {})) {
                                        window.open(url, '_blank');
                                    }
                                }}
                            >
                                {t('moreInformation')}
                            </ButtonLink>
                        }
                    ></Callout>
                </Box>
            </>
        );
    };

    return (
        <SkeletonMobile
            header={
                <div className='roaming-page__header'>
                    {!(isWebviewAndroid || isWebviewIOS) && (
                        <>
                            <div className='roaming-page__header-navigationBar'>
                                <NavigationBar
                                    title={t('roaming')}
                                    onBack={() => {
                                        navigate(`/configure-line`, {
                                            state: { productId: infoSIM?.productId },
                                        });
                                    }}
                                />
                            </div>
                            <div
                                className='roaming-page__header-return'
                                onClick={() => {
                                    navigate(`/configure-line`);
                                }}
                            >
                                <div className='roaming-page__header-return-icon'>
                                    <IconChevronLeftRegular color={skinVars.colors.textPrimary} size={20} />
                                </div>
                                <Text2 medium color={skinVars.colors.textPrimary}>
                                    {t('returnResponsive')}
                                </Text2>
                            </div>
                        </>
                    )}
                    <div className='roaming-page__breadcrumbs'>
                        <ThemeVariant>
                            <BreadCrumbsWrapper
                                title={t('roaming')}
                                breadcrumbs={
                                    !isConvergent
                                        ? [
                                              { title: t('breadcrumbsHome'), url: '/home' },
                                              {
                                                  title: t('lineManagement'),
                                                  url: `/configure-line`,
                                              },
                                          ]
                                        : [
                                              { title: t('breadcrumbsHome'), url: '/home' },
                                              {
                                                  title: getRoleLine(JSON.parse(consumptions), infoSIM?.productPsId!),
                                                  url: `/mobile?productPsId=${infoSIM?.productPsId}`,
                                              },
                                              {
                                                  title: t('lineManagement'),
                                                  url: `/configure-line`,
                                              },
                                          ]
                                }
                            />
                        </ThemeVariant>
                    </div>
                    <div className='roaming-page__header-number'>
                        <Text6 color={skinVars.colors.textPrimary}>{formatMobileNumber(infoSIM?.number)}</Text6>
                    </div>
                </div>
            }
            body={loading ? <Spinner size={24} /> : body()}
        ></SkeletonMobile>
    );
};

export default Roaming;
